import React, { useEffect, useMemo } from 'react';

import { Field, Form, Formik, useField, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { DEFAULT_PROJECT_ROLE } from './AddToProjectModal';
import { DIALOG_BUTTON, FILLED_FIELD, FILLED_SELECT, ROW_SELECT, SOFT_BUTTON } from './organisationUserFormat';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

const FORM_ID = 'organisation-user-create-form';
const ORGANISATION_USER = 'Organisation User';

// The panel's label column and sentence-case labels.
const ROWS = 'tw-grid tw-grid-cols-[150px_minmax(0,1fr)] tw-items-start tw-gap-x-3 tw-gap-y-3';
const LABEL = 'tw-mb-0 tw-pt-2.5 tw-text-0.5sm tw-font-normal tw-normal-case tw-text-charcoal-75';

const schema = () =>
    Yup.object({
        firstName: Yup.string()
            .trim()
            .required($t('First name is required'))
            .min(2, $t('First name must be at least {{min}} characters', { min: 2 }))
            .max(250, $t('First name must be at most {{max}} characters', { max: 250 })),
        lastName: Yup.string()
            .trim()
            .required($t('Last name is required'))
            .min(2, $t('Last name must be at least {{min}} characters', { min: 2 }))
            .max(250, $t('Last name must be at most {{max}} characters', { max: 250 })),
        email: Yup.string().trim().email($t('Enter a valid email address')).required($t('Email is required')),
        roleId: Yup.string().required($t('Organisation role is required')),
    });

function FieldError({ name }) {
    const [, meta] = useField(name);

    return meta.touched && meta.error ? <div className="tw-text-sm tw-text-red-500">{meta.error}</div> : null;
}

function ProjectRows({ projects, projectRoles }) {
    const { values, setFieldValue } = useFormikContext();

    return (
        <table className="tw-w-full tw-text-sm">
            <thead>
                <tr className="tw-border-b tw-border-grey tw-text-xs tw-text-charcoal-75">
                    <th className="tw-py-2 tw-pe-3 tw-text-start tw-font-medium">{$t('Project')}</th>
                    <th className="tw-py-2 tw-text-start tw-font-medium">{$t('Project role')}</th>
                </tr>
            </thead>
            <tbody>
                {projects.map((project) => {
                    const key = `p${project.value}`;
                    const selected = values.projects[key]?.selected ?? false;

                    return (
                        <tr key={project.value} className="tw-border-b tw-border-grey last:tw-border-b-0">
                            <td className="tw-py-2.5 tw-pe-3 tw-text-charcoal">
                                <CheckboxInput
                                    label={project.label}
                                    checked={selected}
                                    onChange={(event) => setFieldValue(`projects.${key}.selected`, event.target.checked)}
                                />
                            </td>
                            <td className="tw-py-2.5">
                                <Field
                                    as="select"
                                    name={`projects.${key}.role`}
                                    disabled={!selected}
                                    aria-label={$t('Project role on {{project}}', { project: project.label })}
                                    className={`select-input ${ROW_SELECT}`}
                                >
                                    {projectRoles.map((name) => (
                                        <option key={name} value={name}>
                                            {$t(name)}
                                        </option>
                                    ))}
                                </Field>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

/**
 * Add new user: name, email and organisation role, the projects to put them
 * on with a role each, and whether to send the invitation now. Department,
 * venue and module access is set inside each project afterwards.
 */
export default function OrganisationUserCreateModal({ isOpen, storeUrl, roles, projects, projectRoles, onClose, onCreated }) {
    const modal = useModal();
    const toast = useToast();
    const { show, hide } = modal;

    useEffect(() => {
        isOpen ? show() : hide();
    }, [isOpen, show, hide]);

    const defaultRole = projectRoles.includes(DEFAULT_PROJECT_ROLE) ? DEFAULT_PROJECT_ROLE : (projectRoles[0] ?? '');
    const initialValues = useMemo(
        () => ({
            firstName: '',
            lastName: '',
            email: '',
            roleId: String(roles.find((role) => role.name === ORGANISATION_USER)?.id ?? roles[0]?.id ?? ''),
            projects: Object.fromEntries(projects.map((project) => [`p${project.value}`, { selected: false, role: defaultRole }])),
            sendInvite: true,
        }),
        [roles, projects, defaultRole],
    );

    const submit = (values, { setSubmitting, resetForm }) => {
        const payload = {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            rolesIds: values.roleId ? [Number(values.roleId)] : [],
            projects: Object.entries(values.projects)
                .filter(([, project]) => project.selected)
                .map(([key, project]) => ({ event_id: Number(key.slice(1)), role: project.role })),
            sendInvite: values.sendInvite,
        };

        return axios
            .post(storeUrl, payload)
            .then(({ data }) => {
                if (data?.exceeded) {
                    toast.error($t('You have exceeded the designated number of users. Please contact the InBudget admin.'));
                } else {
                    toast.success(values.sendInvite ? $t('The user was added and invited') : $t('The user was added'));
                }
                resetForm();
                onCreated();
                onClose();
            })
            .catch((e) => {
                const response = e?.response?.data;

                if (response?.existing_organization_user && response?.already_confirmed_organization_user) {
                    toast.error($t('The user is already in the organization, to edit the information click the edit button in the list.'));
                } else if (response?.existing_organization_user) {
                    toast.error($t('The user is already invited to the organization, to send a new invitation click Resend invite in the list.'));
                } else {
                    toast.error(response?.message || $t('Something went wrong'));
                }
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal modal={modal} parentContainerClasses="tw-max-w-2xl">
            <Formik enableReinitialize initialValues={initialValues} validationSchema={schema()} onSubmit={submit}>
                {({ isSubmitting, resetForm }) => {
                    const close = () => {
                        resetForm();
                        onClose();
                    };

                    return (
                        <>
                            <ModalHeader title={$t('Add new user')} close={close} />
                            <Form id={FORM_ID} className="tw-space-y-4">
                                <div className={ROWS}>
                                    <label htmlFor="create-user-first-name" className={LABEL}>
                                        {$t('Name')}
                                    </label>
                                    <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                                        <div className="tw-space-y-1">
                                            <Field
                                                id="create-user-first-name"
                                                name="firstName"
                                                type="text"
                                                className={`text-input ${FILLED_FIELD}`}
                                                placeholder={$t('First name')}
                                                aria-label={$t('First name')}
                                            />
                                            <FieldError name="firstName" />
                                        </div>
                                        <div className="tw-space-y-1">
                                            <Field
                                                name="lastName"
                                                type="text"
                                                className={`text-input ${FILLED_FIELD}`}
                                                placeholder={$t('Last name')}
                                                aria-label={$t('Last name')}
                                            />
                                            <FieldError name="lastName" />
                                        </div>
                                    </div>

                                    <label htmlFor="create-user-email" className={LABEL}>
                                        {$t('Email')}
                                    </label>
                                    <div className="tw-space-y-1">
                                        <Field
                                            id="create-user-email"
                                            name="email"
                                            type="email"
                                            className={`text-input ${FILLED_FIELD}`}
                                            placeholder="name@organisation.org"
                                        />
                                        <FieldError name="email" />
                                    </div>

                                    <label htmlFor="create-user-role" className={LABEL}>
                                        {$t('Organisation role')}
                                    </label>
                                    <Field as="select" id="create-user-role" name="roleId" className={`select-input ${FILLED_SELECT}`}>
                                        {roles.map((role) => (
                                            <option key={role.id} value={String(role.id)}>
                                                {role.name === ORGANISATION_USER ? $t('Organisation user') : $t(role.name)}
                                            </option>
                                        ))}
                                    </Field>

                                    <span className={LABEL}>{$t('Projects')}</span>
                                    <div className="tw-space-y-2">
                                        {projects.length === 0 ? (
                                            <p className="tw-mb-0 tw-pt-2.5 tw-text-sm tw-text-charcoal-50">{$t('This organisation has no projects yet.')}</p>
                                        ) : (
                                            <ProjectRows projects={projects} projectRoles={projectRoles} />
                                        )}
                                        <p className="tw-mb-0 tw-text-xs tw-text-charcoal-50">
                                            {$t('Department, venue and module access is set in each project after the user is added.')}
                                        </p>
                                    </div>

                                    <span className={LABEL}>{$t('Invite')}</span>
                                    <div className="tw-pt-2">
                                        <Field name="sendInvite">
                                            {({ field }) => (
                                                <CheckboxInput
                                                    label={$t('Send invitation email now')}
                                                    name={field.name}
                                                    checked={!!field.value}
                                                    onChange={field.onChange}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                </div>

                                <div className="modal__footer">
                                    <Button variant="secondary" className={SOFT_BUTTON} onClick={close} disabled={isSubmitting}>
                                        {$t('Cancel')}
                                    </Button>
                                    <Button variant="primary" className={DIALOG_BUTTON} type="submit" loading={isSubmitting} disabled={isSubmitting}>
                                        {$t('Add user')}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    );
                }}
            </Formik>
        </Modal>
    );
}
